/**
 * Atomic Design System
 * 
 * A minimal layer that:
 * 1. Adds NEW tokens that don't exist in tenant system (spacing, typography scales, etc.)
 * 2. Provides a hook to consume EXISTING tenant theme alongside new atomic tokens
 * 3. Offers component-specific token mappings for common patterns
 * 
 * @example
 * ```tsx
 * // Use new atomic tokens
 * import { spacing, typography } from 'design-system';
 * const style = { padding: spacing.lg, fontSize: typography.fontSize.md };
 * 
 * // Use tenant theme + atomic tokens together
 * import { useDesignSystem } from 'design-system';
 * function MyComponent() {
 *   const { theme, tokens } = useDesignSystem();
 *   return <div style={{ 
 *     color: theme.colors.primaryDark,      // From tenant
 *     padding: tokens.spacing.lg,           // From atomic
 *   }} />;
 * }
 * ```
 */

import { useThemeContext } from 'contexts/theme';
import type { ThemeConfig, ThemeNames } from '@tenants/types';

export { createMuiThemeFromTenant } from './create-mui-theme';

// Export all tokens from barrel
export * from './tokens';

// Re-import for use in this file
import {
  spacing,
  typography,
  iconSizes,
  containerSizes,
  semanticColors,
  colorScales,
  overlayColors,
  effects,
  zIndex,
} from './tokens';
import { createMuiThemeFromTenant } from './create-mui-theme';

/**
 * Design System Context
 * 
 * Combines tenant theme with atomic tokens for easy access in components.
 */
export interface DesignSystemContext {
  /** Tenant theme (existing system) */
  theme: ThemeConfig<ThemeNames>;
  /** Tenant name */
  tenantName: ThemeNames;
  /** New atomic tokens */
  tokens: {
    spacing: typeof spacing;
    typography: typeof typography;
    iconSizes: typeof iconSizes;
    containerSizes: typeof containerSizes;
    semanticColors: typeof semanticColors;
    colorScales: typeof colorScales;
    overlayColors: typeof overlayColors;
    effects: typeof effects;
    zIndex: typeof zIndex;
  };
  /** Component-specific token mappings */
  components: ReturnType<typeof getComponentTokens>;
}

/**
 * Component Token Mappings
 * 
 * Pre-configured token combinations for common UI patterns.
 * These map tenant theme colors + atomic tokens into semantic component APIs.
 */
function getComponentTokens(theme: ThemeConfig<ThemeNames>) {
  return {
    /** Badge component tokens */
    badge: {
      /** Success badge (green) - e.g., Open House, Virtual Tour */
      success: {
        bg: semanticColors.success,
        color: semanticColors.white,
        fontSize: typography.fontSize.xs,
        fontWeight: typography.fontWeight.semibold,
        padding: `${spacing.sm} ${spacing.md}`,
        gap: spacing.xs,
        borderRadius: theme.buttons.borderRadius, // Use tenant button radius
      },
      
      /** Overlay badge (dark transparent) - e.g., carousel badges */
      overlay: {
        bg: semanticColors.overlay,
        color: semanticColors.white,
        fontSize: typography.fontSize.xs,
        fontWeight: typography.fontWeight.semibold,
        padding: `${spacing.sm} ${spacing.md}`,
        gap: spacing.xs,
        borderRadius: theme.buttons.borderRadius,
      },
      
      /** Recency badge (light with border) */
      recency: {
        bg: semanticColors.recencyBg,
        color: theme.colors.primaryDark, // Use tenant primary color
        fontSize: typography.fontSize.sm,
        fontWeight: typography.fontWeight.medium,
        padding: `${spacing.sm} ${spacing.md}`,
        gap: spacing.sm,
        border: `1px solid ${semanticColors.recencyBorder}`,
        borderRadius: theme.buttons.borderRadius,
      },
      
      /** Image counter badge (white with blur) */
      imageCounter: {
        bg: 'rgba(255, 255, 255, 0.8)',
        color: theme.colors.primaryDark,
        fontSize: typography.fontSize.md,
        fontWeight: typography.fontWeight.regular,
        padding: `${spacing.sm} ${spacing.md}`,
        borderRadius: theme.buttons.borderRadius,
        backdropFilter: effects.backdropFilters.light,
        letterSpacing: typography.letterSpacing.normal,
      },
    },
    
    /** Icon container tokens */
    icon: {
      /** Circular icon container */
      circular: {
        bg: semanticColors.lightGray,
        size: containerSizes.circularIcon.md,
        iconSize: iconSizes.lg,
        borderRadius: '50%',
      },
    },
    
    /** Text style tokens */
    text: {
      /** Price display */
      price: {
        fontSize: typography.fontSize.xl,
        fontWeight: typography.fontWeight.semibold,
        lineHeight: typography.lineHeight.loose,
        color: theme.colors.primaryDark, // Use tenant color
        fontFamily: theme.fonts.heading, // Use tenant font
      },
      
      /** Address display */
      address: {
        fontSize: typography.fontSize.lg,
        fontWeight: typography.fontWeight.medium,
        lineHeight: typography.lineHeight.base,
        color: theme.colors.primaryDark,
        fontFamily: theme.fonts.text,
      },
      
      /** Property type */
      propertyType: {
        fontSize: typography.fontSize.md,
        fontWeight: typography.fontWeight.regular,
        color: theme.colors.primaryDark,
        fontFamily: theme.fonts.text,
      },
      
      /** Stats labels */
      stats: {
        fontSize: typography.fontSize.lg,
        fontWeight: typography.fontWeight.medium,
        lineHeight: typography.lineHeight.base,
        color: theme.colors.primaryDark,
        fontFamily: theme.fonts.text,
      },
    },
  };
}

/**
 * Hook: Use Design System
 * 
 * Provides access to both tenant theme and atomic tokens in a single hook.
 * This is the primary way to consume the design system in components.
 * 
 * @returns Combined tenant theme + atomic tokens + component mappings
 * 
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { theme, tokens, components } = useDesignSystem();
 *   
 *   return (
 *     <div style={{
 *       // Use tenant theme
 *       color: theme.colors.primaryDark,
 *       fontFamily: theme.fonts.heading,
 *       
 *       // Use atomic tokens
 *       padding: tokens.spacing.lg,
 *       fontSize: tokens.typography.fontSize.md,
 *       
 *       // Use component mappings
 *       ...components.badge.success,
 *     }}>
 *       Content
 *     </div>
 *   );
 * }
 * ```
 */
export function useDesignSystem(): DesignSystemContext {
  const { theme, themeName } = useThemeContext();
  
  return {
    theme,
    tenantName: themeName,
    tokens: {
      spacing,
      typography,
      iconSizes,
      containerSizes,
      semanticColors,
      colorScales,
      overlayColors,
      effects,
      zIndex,
    },
    components: getComponentTokens(theme),
  };
}

/**
 * Helper: Get Component Tokens (without hook)
 * 
 * For use outside of React components (e.g., in utilities, tests).
 * 
 * @param theme - Tenant theme config
 * @returns Component token mappings
 */
export function getComponentTokensForTheme(theme: ThemeConfig<ThemeNames>) {
  return getComponentTokens(theme);
}

/**
 * Hook: Use MUI Theme
 *
 * Builds a per-tenant MUI Theme object from the current tenant's design tokens.
 * Pass the result directly to MUI's ThemeProvider — no manual color props needed
 * on any atomic component.
 *
 * @example
 * ```tsx
 * // In _app.tsx
 * import { useMuiTheme } from 'design-system';
 *
 * function App() {
 *   const muiTheme = useMuiTheme();
 *   return <MUIThemeProvider theme={muiTheme}>...</MUIThemeProvider>;
 * }
 * ```
 */
export function useMuiTheme() {
  const { theme } = useDesignSystem();
  return createMuiThemeFromTenant(theme);
}
